import React, { useRef, useState } from 'react';
import { useApiData } from '../../contexts/apiService';

// Client-side guard: reject too-large files before we even upload. 20 MB is reasonable
// (it enforces the same via Flask MAX_CONTENT_LENGTH).
// Rationale: Cloud Run hard-caps requests at 32 MiB, and scanned PDFs that go through
// Document AI OCR top out around 20 MB anyway — so 20 MB is the safe shared limit.
// Keep this in sync with the backend MAX_CONTENT_LENGTH if the server limit changes.
const MAX_BYTES = 20 * 1024 * 1024; // 20 MB

interface LabPdfUploadModalProps {
    onClose: () => void;
    // Hands back the extraction result AND the original file, so the review modal
    // can show the PDF side-by-side for number-checking.
    onExtracted: (payload: any, file: File) => void;
    // Fallback when the PDF can't be read: open the review grid empty for manual entry.
    onManualEntry: () => void;
}

const LabPdfUploadModal: React.FC<LabPdfUploadModalProps> = ({ onClose, onExtracted, onManualEntry }) => {
    const { handleUploadLabPdf } = useApiData();
    const fileRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [processing, setProcessing] = useState<boolean>(false);

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setError(null);
        const f = e.target.files?.[0];
        if (!f) {
            setFile(null);
            return;
        }
        if (!f.name.toLowerCase().endsWith('.pdf') && f.type !== 'application/pdf') {
            setError('Only PDF files are accepted.');
            setFile(null);
            return;
        }
        if (f.size > MAX_BYTES) {
            setError('File is larger than 20 MB.');
            setFile(null);
            return;
        }
        setFile(f);
    };

    const onProcess = async () => {
        if (!file || processing) return;
        setError(null);
        setProcessing(true);
        try {
            const result = await handleUploadLabPdf(file);
            if (!result || result.success === false) {
                setError(result?.message || 'Failed to extract the PDF. Try again or use the manual form.');
                setProcessing(false);
                return;
            }
            const pots = result.pots || result.data?.pots || [];
            if (!Array.isArray(pots) || pots.length === 0) {
                setError('No samples were detected in this PDF. It may be scanned without clear numeric data, or the page needs manual entry.');
                setProcessing(false);
                return;
            }
            onExtracted(result, file);
        } catch (e: any) {
            setError(e?.message || 'Unexpected error while extracting the PDF.');
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="carrier-modal-overlay" onClick={processing ? undefined : onClose}>
            <div
                className="carrier-modal-content labPdf-upload-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 className="labPdf-title">Upload Lab Report PDF</h2>
                <p className="labPdf-subtitle">
                    Pick the PDF report issued by the lab. We'll extract samples and let you review
                    before saving.
                </p>

                <div className="labPdf-upload-body">
                    <div className="labPdf-filepicker">
                        <button
                            type="button"
                            className="labPdf-btn labPdf-btn--ghost"
                            onClick={() => fileRef.current?.click()}
                            disabled={processing}
                        >
                            Choose File
                        </button>
                        <span className="labPdf-filepicker-name">
                            {file ? `${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)` : 'No file selected'}
                        </span>
                    </div>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        onChange={onFileChange}
                        disabled={processing}
                        className="labPdf-file-input"
                    />
                    <div className="labPdf-file-hint">
                        PDF only · up to 20 MB · extraction usually takes 10–30 seconds
                    </div>
                    {error && <div className="labPdf-error">{error}</div>}
                </div>

                {processing && (
                    <div className="labPdf-processing">
                        <div className="spinner" />
                        <div className="labPdf-processing-text">
                            Extracting samples from the PDF… this can take up to a minute.
                        </div>
                    </div>
                )}

                <div className="labPdf-actions">
                    <button
                        type="button"
                        className="labPdf-btn labPdf-btn--ghost"
                        onClick={onManualEntry}
                        disabled={processing}
                    >
                        Enter manually
                    </button>                    
                    
                    <button
                        type="button"
                        className="labPdf-btn labPdf-btn--ghost"
                        onClick={onClose}
                        disabled={processing}
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        className="labPdf-btn labPdf-btn--primary"
                        onClick={onProcess}
                        disabled={!file || processing}
                    >
                        {processing ? 'Processing…' : 'Process'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LabPdfUploadModal;
